import { QuestionMarkIcon } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useCreateMarketShopPayment } from '@/api/market-shop/checkout/create-market-shop-payment'
import { Button } from '@/components/business/market-shop/button'
import { Alert } from '@/components/ui/alert'
import { Icon } from '@/components/ui/icon'
import { Overlay } from '@/components/ui/overlay'
import { PopupRequiredProps } from '@/factories'
import { useSSE } from '@/hooks'
import { useMarketShopStore } from '@/pages/market-shop/market-shop.store'
import { getApiErrorMessage } from '@/utils/error'

import * as S from './contactless-payment-modal.styled'

const SUPPORT_EMAIL = 'hello@elevatedliving.com'

export type ContactlessPaymentModalProps = PopupRequiredProps & {
  onSuccessPayment: (transactionId: string) => void
}

export const ContactlessPaymentModal = ({
  onSuccessPayment,
  onClose,
}: ContactlessPaymentModalProps) => {
  const { t } = useTranslation(['market-shop'])
  const { sessionId } = useMarketShopStore()

  const [error, setError] = useState('')

  const { connect, disconnect } = useSSE(
    `${import.meta.env.VITE_API_URL}/sse?channels=${sessionId}&public=1`,
    {
      events: {
        payment_failed: () => {
          setError(t('market-shop:contactlessPaymentModal.genericPaymentError'))
        },
        payment_succeeded: (event) => {
          try {
            const parsed = JSON.parse(event.data)
            if ('transactionId' in parsed && typeof parsed.transactionId === 'string') {
              onClose()
              onSuccessPayment(parsed.transactionId)
              return
            }

            throw new Error("Couldn't extract transactionId from successful payment")
          } catch {
            // NOTE: ideally, it should never happen, but this case just handles either the low possibility
            // of broken JSON data or missing transactionId in response from SSE
            onClose()
            onSuccessPayment(`ORDER-${Date.now()}`)
          }
        },
      },
    },
  )
  const { mutateAsync: createPayment } = useCreateMarketShopPayment()

  useEffect(() => {
    const handlePayment = async () => {
      try {
        // NOTE: Firstly, connect to SSE to be able to react to successful/rejected payments,
        // then, create a payment using the sessionId
        await connect()
        await createPayment({ sessionId })
      } catch (error) {
        disconnect()
        setError(getApiErrorMessage(error))
      }
    }

    void handlePayment()
  }, [])

  return (
    <Overlay closeOnOverlayClick={false} closeOnEscapeKey={false} onClose={onClose}>
      {({ handleClose, getFloatingProps, setFloating }) => (
        <S.ModalContent ref={setFloating} {...getFloatingProps()}>
          <S.Icon
            src="/images/market-shop/contactless.png"
            alt={t('market-shop:contactlessPaymentModal.imageAlt')}
          />

          <S.Title>{t('market-shop:contactlessPaymentModal.title')}</S.Title>

          <S.SubTitle>{t('market-shop:contactlessPaymentModal.subtitle')}</S.SubTitle>

          {error && <Alert type="error" title={error} />}

          <S.SupportSection>
            <Icon icon={QuestionMarkIcon} size="2rem" weight="bold" />
            <p className="payment-modal__support-content">
              <Trans
                components={[
                  <strong />,
                  <a href={`mailto:${SUPPORT_EMAIL}`} rel="noreferrer" target="_blank" />,
                ]}
                i18nKey="contactlessPaymentModal.supportPrompt"
                ns="market-shop"
                values={{ email: SUPPORT_EMAIL }}
              />
            </p>
          </S.SupportSection>

          <Button use="secondary-gray" size="large" onClick={handleClose}>
            {t('market-shop:contactlessPaymentModal.close')}
          </Button>
        </S.ModalContent>
      )}
    </Overlay>
  )
}
